import * as t from 'io-ts';
import uuid from 'uuid-random';

import {ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';

/*
 * Runtime type.
 */

export enum SchedulingLinkQuestionTypesEnum {
  FREEFORM = 'freeform',
  SELECTION = 'selection',
}

const RSchedulingLinkBaseQuestionDirty = t.readonly(
  t.type({
    uid: t.string,
    question: t.string,
  }),
);

const RSchedulingLinkFreeformQuestionDirty = t.intersection([
  RSchedulingLinkBaseQuestionDirty,
  t.type({
    type: t.literal(SchedulingLinkQuestionTypesEnum.FREEFORM),
  }),
]);

const RSchedulingLinkSelectionOptionDirty = t.readonly(
  t.type({
    uid: t.string,
    value: t.string,
  }),
);

const RSchedulingLinkSelectionQuestionDirty = t.intersection([
  RSchedulingLinkBaseQuestionDirty,
  t.type({
    type: t.literal(SchedulingLinkQuestionTypesEnum.SELECTION),
    selectionOptions: t.readonlyArray(RSchedulingLinkSelectionOptionDirty),
  }),
]);

const RSchedulingLinkQuestionDirty = t.union([
  RSchedulingLinkFreeformQuestionDirty,
  RSchedulingLinkSelectionQuestionDirty,
]);

/*
 * Type.
 */

export type SchedulingLinkSelectionOptionModel = t.TypeOf<typeof RSchedulingLinkSelectionOptionDirty>;
export type SchedulingLinkFreeformQuestionModel = t.TypeOf<typeof RSchedulingLinkFreeformQuestionDirty>;
export type SchedulingLinkSelectionQuestionModel = t.TypeOf<typeof RSchedulingLinkSelectionQuestionDirty>;
export const RSchedulingLinkQuestion = t.clean<SchedulingLinkQuestionModel>(
  RSchedulingLinkQuestionDirty as any,
);
export type SchedulingLinkQuestionModel = t.TypeOf<typeof RSchedulingLinkQuestionDirty>;

/*
 * Helpers.
 */

export function isFreeformQuestion(
  question: SchedulingLinkQuestionModel,
): question is SchedulingLinkFreeformQuestionModel {
  return question.type === SchedulingLinkQuestionTypesEnum.FREEFORM;
}

export function isSelectionQuestion(
  question: SchedulingLinkQuestionModel,
): question is SchedulingLinkSelectionQuestionModel {
  return question.type === SchedulingLinkQuestionTypesEnum.SELECTION;
}

/*
 * IO methods.
 */

export function parseSchedulingLinkQuestion(src: any): SchedulingLinkQuestionModel | undefined {
  switch (src.questionType) {
    case 'freeform':
      return {
        uid: uuid(),
        question: src.question,
        type: SchedulingLinkQuestionTypesEnum.FREEFORM,
      };
    case 'selection':
      return {
        uid: uuid(),
        question: src.question,
        type: SchedulingLinkQuestionTypesEnum.SELECTION,
        selectionOptions: (src.selectionOptions || []).map((option: string) => ({
          uid: uuid(),
          value: option,
        })),
      };
    default:
      reportToBugsnag(
        new ErrorWithData('Received unknown question type', {
          questionType: src.questionType,
        }),
      );
      return undefined;
  }
}

export function serializeSchedulingLinkQuestion(question: SchedulingLinkQuestionModel) {
  if (question.type === SchedulingLinkQuestionTypesEnum.FREEFORM && question.question) {
    return {
      question: question.question,
      questionType: SchedulingLinkQuestionTypesEnum.FREEFORM,
    };
  }

  if (question.type === SchedulingLinkQuestionTypesEnum.SELECTION && question.question) {
    return {
      question: question.question,
      questionType: SchedulingLinkQuestionTypesEnum.SELECTION,
      selectionOptions: question.selectionOptions
        .map((option) => option.value)
        .filter((value) => Boolean(value)),
    };
  }

  return undefined;
}
